import React, { useEffect, useMemo, useRef } from 'react';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { useLocation } from 'react-router-dom';

import EventContent from './Components/EventContent.jsx';
import TeamCalendarsSidebar from '../SideBar/TeamCalendarsSidebar';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import { getLocalStorageParseItem, removeLocalStorageItem } from 'utils/localStorageUtils';
import { isOutsideBusinessHours } from 'utils/utils';

function CalendarView({ isCollapsed, toggleSidebar }) {
  // Show sidebar overlay for mobile
  const [showSidebarOverlay, setShowSidebarOverlay] = React.useState(false);
  const calendarRef = useRef(null);
  const sidebarRef = useRef(null);
  const calendarWrapRef = useRef(null);
  const isProgrammaticViewChangeRef = useRef(false);
  const {
    eventList,
    outlookEventList,
    calendarSettings,
    setShowEventPreviewModal,
    setSelectedEvent,
    currentDates,
    setCurrentDates,
    setCurrentView,
    currentView,
    calenderDayClick,
    showFirmEvents,
    isClientCalendar,
  } = useCalendarContext();
  const userContext = useUserDetailsContext();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryEventDate = queryParams.get('date');

  // Fallback to undefined if not provided
  const initialDate = useMemo(() => {
    return queryEventDate || undefined;
  }, [queryEventDate]);

  let timezone = userContext?.userDetails?.timezone;

  const onEventClick = (info) => {
    let eventSelected;
    if (info?.event !== undefined) {
      eventSelected = info.event.toJSON();
    } else {
      eventSelected = {
        extendedProps: info,
        title: info?.title,
        start: info?.meeting_start_time ? info?.meeting_start_time : info?.start,
        end: info?.meeting_end_time ? info?.meeting_end_time : info?.end,
      };
    }
    setSelectedEvent(eventSelected);
    if (eventSelected.extendedProps.res_type === 'user') {
      const arr = eventSelected?.extendedProps?.sk?.split('#');
      const contactId = arr && arr.length > 0 && arr[arr.length - 1];
      const url = contactId ? `/contacts/${contactId}` : '/contacts';
      return url;
    } else {
      setShowEventPreviewModal(true);
    }
  };

  const options = {
    plugins: [multiMonthPlugin, dayGridPlugin, timeGridPlugin, interactionPlugin],

    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next,today',
      center: 'title',
      right: 'multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay',
    },
    allDayText: 'All Day',
    editable: false,
    showNonCurrentDates: true,
    dayPopoverFormat: { month: '2-digit', day: '2-digit', year: 'numeric' },
    events: function (_info, successCallback) {
      let event_list = Array.isArray(eventList)
        ? eventList.map((event) => ({
            ...event,
            allDay: event?.all_day === true,
          }))
        : [];

      //Filter events based on timezone and show business time events from 9am to 5pm
      event_list = event_list?.filter((event) => {
        if (event?.res_type === 'event' && calendarSettings?.show_business_times_only) {
          return !isOutsideBusinessHours(event, timezone);
        } else return true;
      });

      let outlook_event_list = Array.isArray(outlookEventList) ? outlookEventList : [];

      let concatenatedArray = [...event_list, ...outlook_event_list];

      // When showFirmEvents is true, show all events from the API
      // No filtering needed - all events are displayed

      successCallback(concatenatedArray);
    },

    eventContent: EventContent,
    eventClick: onEventClick,

    weekends: calendarSettings?.show_weekends,
    slotMinTime: calendarSettings?.show_business_times_only ? '09:00' : '00:00:00',
    slotMaxTime: calendarSettings?.show_business_times_only ? '17:00' : '24:00:00',
    hiddenDays: calendarSettings?.show_weekends ? [] : [0, 6],
    views: {
      timelineCustom: {
        type: 'timeGrid',
        dateIncrement: { years: 1 },
        slotDuration: { months: 1 },
        visibleRange: function (currentDate) {
          let d = new Date();
          return {
            start: `${d.getFullYear()}-01-01 `,
            end: `${d.getFullYear()}-12-31`,
          };
        },
      },
    },
    slotLaneClassNames: (arg) => {
      return arg.view.type === 'timeGridDay' ? ['cursor-pointer'] : [];
    },
  };

  const mapViewToFullCalendar = (view) => {
    const viewMap = {
      day: 'timeGridDay',
      week: 'timeGridWeek',
      month: 'dayGridMonth',
      year: 'multiMonthYear',
    };
    return viewMap[view] || 'dayGridMonth';
  };

  const handleViewDidMount = (data) => {
    // Only update if it's not a programmatic change
    if (!isProgrammaticViewChangeRef.current && data.view?.type) {
      setCurrentView(data.view?.type);
    }
    isProgrammaticViewChangeRef.current = false;
  };

  const handleDatesSet = (dateInfo) => {
    let current_start = dateInfo?.start;
    let current_end = dateInfo?.end;

    // Update current view when dates are set (this happens when view changes)
    // Only update if it's not a programmatic change
    if (dateInfo?.view?.type && dateInfo.view.type !== currentView && !isProgrammaticViewChangeRef.current) {
      setCurrentView(dateInfo.view.type);
    }
    isProgrammaticViewChangeRef.current = false;

    //to avoid infinite recursion
    if (current_start.valueOf() !== currentDates.currentStartValue || current_end.valueOf() !== currentDates.currentEndValue) {
      setCurrentDates({
        currentStartValue: current_start.valueOf(),
        currentEndValue: current_end.valueOf(),
        currentStartDate: current_start,
        currentEndDate: current_end,
      });
    }
  };

  const generateFormattedEventDetails = (data = {}) => {
    const { title, meeting_start_time, meeting_end_time, sk, pk, icon = '' } = data;

    let formattedEventDetails = {
      allDay: false,
      title: title,
      extendedProps: {
        meeting_start_time,
        meeting_end_time,
        res_type: 'event',
        icon,
        sk,
        pk,
      },
    };
    return formattedEventDetails;
  };

  const handleDateClick = (arg) => {
    if (arg?.jsEvent?.target?.classList?.contains('fc-daygrid-day-number')) {
      const calendarApi = arg.view.calendar;
      calendarApi.changeView('timeGridDay', arg.date);
    }
    if (arg.view.type === 'timeGridDay') {
      calenderDayClick(arg, true);
    }
  };

  useEffect(() => {
    let selectedEventDetails = getLocalStorageParseItem('stateData')?.viewEvent || location?.state?.viewEvent;

    if (selectedEventDetails) {
      setSelectedEvent(generateFormattedEventDetails(selectedEventDetails));
      setShowEventPreviewModal(true);
      removeLocalStorageItem('stateData');
    }
  }, [location, setSelectedEvent, setShowEventPreviewModal]);

  // Refetch events when firm-wide toggle changes so filter applies immediately
  useEffect(() => {
    const api = calendarRef?.current?.getApi?.();
    if (api && typeof api.refetchEvents === 'function') {
      api.refetchEvents();
    }
  }, [showFirmEvents]);

  // Update FullCalendar view when calendarSettings.default_calendar_view changes
  useEffect(() => {
    if (calendarSettings?.default_calendar_view) {
      const api = calendarRef?.current?.getApi?.();
      if (api && typeof api.changeView === 'function') {
        const fullCalendarView = mapViewToFullCalendar(calendarSettings.default_calendar_view);
        const currentViewType = api.view?.type;
        if (currentViewType !== fullCalendarView) {
          isProgrammaticViewChangeRef.current = true;
          api.changeView(fullCalendarView);
        }
      }
    }
  }, [calendarSettings?.default_calendar_view]);

  // Match sidebar height to calendar height
  useEffect(() => {
    if (isClientCalendar) return; // Skip if client calendar (no sidebar)

    const updateSidebarHeight = () => {
      if (calendarWrapRef.current && sidebarRef.current) {
        const calendarHeight = calendarWrapRef.current.offsetHeight;
        sidebarRef.current.style.height = `${calendarHeight}px`;
      }
    };

    // Update after a short delay to ensure calendar is rendered
    const timer = setTimeout(updateSidebarHeight, 100);

    // Update when window resizes
    window.addEventListener('resize', updateSidebarHeight);

    // Update when calendar view changes
    const api = calendarRef?.current?.getApi?.();
    if (api) {
      api.on('datesSet', updateSidebarHeight);
      api.on('viewDidMount', updateSidebarHeight);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateSidebarHeight);
      if (api) {
        api.off('datesSet', updateSidebarHeight);
        api.off('viewDidMount', updateSidebarHeight);
      }
    };
  }, [currentView, isCollapsed, isClientCalendar]);

  return (
    <>
      {/* Team Members button for mobile - OUTSIDE and above calendar */}
      {!isClientCalendar && (
        <div className="d-block d-md-none w-100 mb-2">
          <button
            type="button"
            aria-label="Show team sidebar"
            className={`p-button p-component btn p-button-secondary outline ${showSidebarOverlay ? ' d-none' : ''}`}
            style={{ zIndex: 100 }}
            onClick={() => setShowSidebarOverlay(true)}
          >
            Team Members
          </button>
          {/* Sidebar overlay for mobile */}
          {showSidebarOverlay && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-stretch"
              style={{ background: 'rgba(0,0,0,0.3)', zIndex: 9999 }}
            >
              <div
                className="bg-white shadow-lg d-flex flex-column h-100"
                style={{ width: '80vw', maxWidth: '320px', borderRadius: '0 8px 8px 0', position: 'relative', padding: 0 }}
              >
                <button
                  type="button"
                  aria-label="Close sidebar"
                  className="btn p-0 border-0 bg-transparent position-absolute"
                  style={{ top: 12, right: 12, fontSize: '1.5rem', cursor: 'pointer', zIndex: 10001 }}
                  onClick={() => setShowSidebarOverlay(false)}
                >
                  {/* Close icon SVG */}
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#555"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
                <div className="pt-5 flex-grow-1 overflow-auto">
                  <TeamCalendarsSidebar isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
                </div>
              </div>
              {/* Click outside to close overlay */}
              <div className="flex-grow-1" onClick={() => setShowSidebarOverlay(false)} />
            </div>
          )}
        </div>
      )}

      <div className="row mx-0 global-calendar-main ">
        <div className="col-12 px-0 d-flex " style={{ gap: 0, alignItems: 'stretch' }}>
          {/* Sidebar for md and up */}
          {!isClientCalendar && (
            <div
              ref={sidebarRef}
              className="calendar-sidebar d-none d-md-block bg-white shadow-sm"
              style={{
                width: isCollapsed ? '52px' : '280px',
                transition: 'width 0.3s ease, height 0.3s ease',
                flexShrink: 0,
                borderRight: '1px solid #e0e0e0',
                borderRadius: '8px 0 0 8px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <TeamCalendarsSidebar isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
            </div>
          )}

          {/* Calendar */}
          <div
            ref={calendarWrapRef}
            className="Calendar-wrap shadow-sm mb-3"
            style={{ borderRadius: isClientCalendar ? '8px' : '0 8px 8px 0', minWidth: 0, flexGrow: 1 }}
          >
            <FullCalendar
              ref={calendarRef}
              {...options}
              dayMaxEvents={2}
              eventBackgroundColor="white"
              eventBorderColor="white"
              eventOverlap={false}
              slotEventOverlap={false}
              viewDidMount={handleViewDidMount}
              datesSet={handleDatesSet}
              lazyFetching={true}
              multiMonthMaxColumns={3}
              dateClick={handleDateClick}
              height={'auto'}
              timeZone={timezone || 'local'}
              initialDate={initialDate}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default CalendarView;
